import React from "react";
import styled from "styled-components";
import Navigation from "../components/Header/nav";
import GitHubButton from "../utils/GitHubButton";
import CodeDisplay from "../utils/CodeDisplay";
import TextShift from "../utils/TextShift";
import RevealOnScroll from "../utils/RevealOnScroll";
import Social1 from "../images/FeaturedProjects/SocialNest/Social1.png";
import Social2 from "../images/FeaturedProjects/SocialNest/Social2.png";
import Social3 from "../images/FeaturedProjects/SocialNest/Social3.png";
import Social4 from "../images/FeaturedProjects/SocialNest/Social4.png";
import Social5 from "../images/FeaturedProjects/SocialNest/Social5.png";
import SocialGif from "../images/FeaturedProjects/SocialNest/SocialGif.gif";

const PageWrapper = styled.div`
  width: 60%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    width: 90%;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 2rem;
`;

const ProjectTitle = styled.h1`
  color: white;
  font-family: "Maitree", sans-serif;
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-family: "Maitree", sans-serif;
  font-size: 1.5rem;
  color: #d2d2d2;
  margin: 20px 0;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 20px;
`;

const SectionText = styled.div`
  text-align: left;
  font-size: 1rem;
  margin-bottom: 20px;
  font-family: "Maitree", sans-serif;
  color: #b5b5b5;
  line-height: 1.6;
`;

const SubSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SubTitle = styled.h3`
  color: #d2d2d2;
  font-family: "Maitree", sans-serif;
  font-size: 1.1rem;
  margin-bottom: 1rem;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
`;

const SocialNest = () => {
  const neo4jSetupCode = `
const neo4j = require("neo4j-driver");

class neojsAccess {
  constructor(user, uri, password) {
    this.user = user;
    this.uri = uri;
    this.password = password;
  }

  async fetchGraphData() {
    const driver = neo4j.driver(uri, neo4j.auth.basic(this.user, this.password));
    const session = driver.session();
    try {
      const result = await session.run(\`MATCH (n)-[r]->(m) RETURN n,r,m\`);
      return { result };
    } finally {
      await session.close();
    }
  }
}`;

  const serverSetupCode = `
let express = require("express");
require("dotenv").config();
let neojsAccess = require("./neojsAccess");
const bodyParser = require("body-parser");
const portNumber = 3000;

let app = express();
app.use(express.static(__dirname + "/public"));
app.use(bodyParser.json());

let Neo = new neojsAccess(
  process.env.NEO_USER,
  process.env.NEO_URI,
  process.env.NEO_PASS
);

app.post("/addRelationship", async (request, response) => {
  const { person1FirstName, person1LastName, person2FirstName, person2LastName } = request.body;
  try {
    await Neo.addRelationship(person1FirstName, person1LastName, person2FirstName, person2LastName);
    response.send("Relationship added successfully!");
  } catch (error) {
    response.status(500).send("An error occurred while adding the relationship.");
  }
});`;

  const relationshipCode = `
async addRelationship(person1FirstName, person1LastName, person2FirstName, person2LastName) {
  const session = this.driver.session();
  
  const person1 = { name_f: person1FirstName, name_l: person1LastName, id: generateUniqueId() };
  const person2 = { name_f: person2FirstName, name_l: person2LastName, id: generateUniqueId() };
  
  try {
    await session.run(
      \`MERGE (a:Person {personID: $id1, \`First Name\`: $name_f1, \`Last Name\`: $name_l1})
       MERGE (b:Person {personID: $id2, \`First Name\`: $name_f2, \`Last Name\`: $name_l2})
       MERGE (a)-[r:FRIENDS_WITH]->(b)
       RETURN a, b, r\`,
      {
        id1: person1.id, name_f1: person1.name_f, name_l1: person1.name_l,
        id2: person2.id, name_f2: person2.name_f, name_l2: person2.name_l
      }
    );
  } finally {
    await session.close();
  }
}`;

  return (
    <>
      <PageWrapper>
        <RevealOnScroll>
          <TitleWrapper>
            <TextShift 
              text="SocialNest" 
              fontFamily="moret-variable"
              fontSize="2rem"
            />
            <GitHubButton url="https://github.com/noahkrem/SocialNest" />
          </TitleWrapper>
        </RevealOnScroll>
        <Section>
          <ProjectImage src={Social1} alt="SocialNest Screenshot 1" />
          <ProjectImage src={Social2} alt="SocialNest Screenshot 2" />
          <ProjectImage src={Social3} alt="SocialNest Screenshot 3" />
          <ProjectImage src={Social4} alt="SocialNest Screenshot 4" />
          <ProjectImage src={Social5} alt="SocialNest Screenshot 5" />
          <ProjectImage src={SocialGif} alt="SocialNest Demo" />
        </Section>

        <Section>
          <SectionTitle>Overview</SectionTitle>
          <SectionText>
            SocialNest is a modern social networking platform that leverages the power of Neo4j's graph database for efficient relationship management. Built with React and Node.js, it provides a seamless user experience while demonstrating advanced database modeling and RESTful API implementation.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>Neo4j Database Integration</SectionTitle>
          <SectionText>
            The backend is powered by Neo4j, a graph database that excels at managing complex social relationships. Our `neojsAccess` class provides an elegant interface for database operations, making it easy to:

            The `neojsAccess` class provides an interface for database operations, including querying and traversing social connections, managing user relationships efficiently, storing and retrieving user profile data, and handling complex friend networks.
          </SectionText>
          <CodeDisplay code={neo4jSetupCode} language="javascript" />
        </Section>

        <Section>
          <SectionTitle>Server Architecture</SectionTitle>
          <SectionText>
            The Express.js server forms the backbone of SocialNest, providing:

            The server provides RESTful API endpoints for user interactions, robust middleware for request handling, secure user authentication, efficient static file serving, and comprehensive error handling.
          </SectionText>
          <CodeDisplay code={serverSetupCode} language="javascript" />
        </Section>

        <Section>
          <SectionTitle>Relationship Management</SectionTitle>
          <SectionText>
            At its core, SocialNest excels at managing social connections through:

            SocialNest manages social connections through an intuitive friend request system, real-time relationship updates, efficient graph traversal for friend suggestions, and secure relationship validation.
          </SectionText>
          <CodeDisplay code={relationshipCode} language="javascript" />
        </Section>

     

        <Section>
          <SectionTitle>Core Features</SectionTitle>
          <SubSection>
            <SubTitle>Social Network Infrastructure</SubTitle>
            <SectionText>
              Built on Node.js and Express, SocialNest implements a robust RESTful API architecture with comprehensive 
              middleware for request processing and static file serving. The system utilizes environment-based 
              configuration for flexible deployment across different environments. At its core, Neo4j's graph database 
              powers sophisticated data modeling for social relationships, leveraging the Cypher query language for 
              efficient relationship queries while maintaining ACID compliance for data integrity.
            </SectionText>
          </SubSection>

          <SubSection>
            <SubTitle>Network Management & Visualization</SubTitle>
            <SectionText>
              Users can create and manage their profiles while establishing bidirectional friendship connections 
              within the network. The platform features an interactive network graph visualization system with zoom 
              and pan capabilities, node highlighting, and real-time updates. This visual representation helps users 
              understand and navigate their social connections intuitively.
            </SectionText>
          </SubSection>

          <SubSection>
            <SubTitle>Data Operations & Security</SubTitle>
            <SectionText>
              The platform implements comprehensive CRUD operations for users and relationships, utilizing efficient 
              graph traversal algorithms. Data integrity is maintained through robust validation and secure database 
              operations. Performance is optimized through connection pooling, error boundary implementation, and 
              carefully optimized database queries.
            </SectionText>
          </SubSection>

          <SubSection>
            <SubTitle>User Experience & API Integration</SubTitle>
            <SectionText>
              The user interface features a clean, modern design with intuitive navigation and responsive layouts. 
              Interactive data input forms are complemented by appropriate loading states and error handling. The 
              RESTful API provides comprehensive endpoints for user management, relationship handling, network data 
              retrieval, and sophisticated graph queries, enabling seamless integration and scalability.
            </SectionText>
          </SubSection>
        </Section>
      </PageWrapper>
    </>
  );
};

export default SocialNest;
